import { AgreementModel } from "#src/models/AgreementModel.ts";
import BillingCategoryModel from "#src/models/BillingCategoryModel.ts";
import { ChargeModel } from "#src/models/ChargeModel.ts";
import ClassTimeModel from "#src/models/ClassTimeModel.ts";
import CouponModel from "#src/models/CouponModel.ts";
import { CreditModel } from "#src/models/CreditModel.ts";
import LocationModel from "#src/models/LocationModel.ts";
import MembershipOfferingModel from "#src/models/MembershipOfferingModel.ts";
import { OrganizationModel } from "#src/models/OrganizationModel.ts";
import PrivateSerieModel from "#src/models/PrivateSerieModel.ts";
import ProgramModel from "#src/models/ProgramModel.ts";
import StudentInfoRequestModel from "#src/models/StudentInfoRequestModel.ts";
import UserModel from "#src/models/UserModel.ts";
import { ZoneModel } from "#src/models/ZoneModel.ts";
import type { RegistrationDiscount } from "#src/utils/calculateDiscounts.ts";
import {
  ParticipantTypes,
  PrivateOfferingTypes,
} from "#src/utils/constants.ts";
import {
  AgeType,
  Day,
  Fee,
  Frequency,
  OfferingStatus,
  PaymentFrequency,
  TimeType,
  communications,
  currentOrganization,
  tags,
} from "#types/code-generator.ts";

export const isOffering = (item): item is OfferingModel =>
  item instanceof OfferingModel;

class OfferingModel {
  additionalFeeAmount: number = null;
  additionalFeeBillingCategory: BillingCategoryModel = null;
  additionalFeeTitle: string = null;
  addons: { id: string }[] = [];
  agreements: AgreementModel[] = [];
  allowAdditionalStudents: boolean = false;
  allowExtension: boolean = true;
  allowNonMembers: boolean = true;
  applyAnnualFee: boolean = false;
  atAccountHome: boolean = false;
  availableDays: Day[] = [
    Day.Monday,
    Day.Tuesday,
    Day.Wednesday,
    Day.Thursday,
    Day.Friday,
  ];
  billingCategory: BillingCategoryModel;
  charges: ChargeModel[] = [];
  classTimes: ClassTimeModel[] = [];
  collectTaxes: boolean = false;
  communications: communications["communications"] = [];
  coupons: CouponModel[] = [];
  credits: CreditModel[] = [];
  customerInfoRequests: string[] = [];
  department: currentOrganization["organization"]["departments"][number] = null;
  departmentId: string;
  description: string = null;
  discounts: RegistrationDiscount[] = [];
  duration: number = 30;
  endDate: Date = null;
  excludedDates: Date[] = [];
  extensionDiscounts: RegistrationDiscount[] = [];
  extensionFreePresentation: string;
  extensionMinAttendances: number = null;
  extensionMaxAttendances: number = null;
  extensionPrice: number = null;
  extensionPriceAdjustments: { id: string; min: number; price: number }[] = [];
  fees: Fee[] = [];
  firstLessonStart: Date = null;
  firstLessonStartDays: number = null;
  freePresentation: string;
  frequency: Frequency = Frequency.OneTime;
  hideDefaultStaff: boolean = true;
  instructor: UserModel = null;
  id: string = null;
  location: LocationModel;
  maxAge: number = null;
  maxAgeType: AgeType = null;
  maxInitial: number;
  membershipOfferings: MembershipOfferingModel[] = [];
  minAge: number = null;
  minAgeType: AgeType = null;
  minAttendances: number = null;
  minParticipants: number = null;
  maxParticipants: number = null;
  package: boolean = false;
  paymentFrequency: PaymentFrequency = PaymentFrequency.Upfront;
  order: number;
  organization: OrganizationModel;
  participantType: string = ParticipantTypes.Participant;
  pass: boolean = false;
  price: number = null;
  priceAdjustments: { id: string; min: number; price: number }[] = [];
  priceForAdditionalStudents: number = null;
  privateSeries: PrivateSerieModel[] = [];
  program: ProgramModel = null;
  prorateExcludedDates: boolean = true;
  publishDate: Date = null;
  publishOnWebsite: boolean = true;
  requestStartDate: boolean = true;
  registrationClose: Date = null;
  registrationOpen: Date = null;
  restrictReschedule: number = null;
  restrictRescheduleType: TimeType = null;
  startDate: Date = null;
  status: OfferingStatus;
  studentInfoRequests: StudentInfoRequestModel[] = [];
  tags: tags["tags"] = [];
  template: boolean = false;
  title: string = null;
  type: string = "Private Lesson";
  unpublishDate: Date = null;
  zone: ZoneModel = null;

  constructor(data) {
    Object.assign(this, data);

    if (data) {
      if (!data.type) {
        this.type = PrivateOfferingTypes["Private Lesson"];
      }
      if (!data.participantType) {
        this.participantType = ParticipantTypes.Participant;
      }
    }
  }

  get active() {
    return (
      this.status === OfferingStatus.InProgress ||
      this.status === OfferingStatus.Upcoming
    );
  }
}

export default OfferingModel;
